// frontend/src/pages/CheckoutPage.js
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { ordersApi } from '../api/orders';
import { paymentApi } from '../api/paymentApi';
import { zonesApi } from '../api/zones'; 
import helpers from '../utils/helpers';
import { 
    FaLock, FaTruck, FaCreditCard, FaMoneyBillWave, 
    FaUser, FaSave, FaCheckSquare, FaSquare, FaCheckCircle, FaSpinner 
} from 'react-icons/fa';

function CheckoutPage() {
  const { cartItems, cartTotal, clearCart } = useCart();
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [successState, setSuccessState] = useState(false);
  const [error, setError] = useState(null);
  
  const [paymentMethod, setPaymentMethod] = useState('online'); 
  const [savedCards, setSavedCards] = useState([]);
  const [saveCard, setSaveCard] = useState(false);

  // --- LOCATION DATA ---
  const [lgas, setLgas] = useState([]);

  // Form State
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    address_line1: '',
    city: '',
    state: '' 
  });

  // 1. Load User Data, Saved Cards, and LGAs
  useEffect(() => {
    const init = async () => {
        try {
            // Fetch LGAs for dropdown
            const lgasData = await zonesApi.getAllLgas();
            setLgas(lgasData || []);

            if (user) {
                // Auto-fill Shipping
                let cityName = user.custom_lga || '';
                if (user.lga_id && lgasData) {
                    const found = lgasData.find(l => l.id === user.lga_id);
                    if (found) cityName = found.lga;
                }

                setFormData({
                    full_name: user.fullName || '',
                    email: user.email || '',
                    phone: user.phone || '',
                    address_line1: user.address || '',
                    city: cityName,
                    state: user.state || ''
                });

                // Fetch Saved Cards
                const cards = await paymentApi.getSavedCards();
                setSavedCards(cards || []);
                if (cards && cards.length > 0) {
                    setPaymentMethod(cards[0].token);
                }
            }
        } catch (err) {
            console.error("Error initializing checkout:", err);
        }
    };
    init();
  }, [user]);

  // --- DYNAMIC LOCATION LOGIC ---
  const groupedLgas = useMemo(() => {
    return lgas.reduce((acc, lga) => {
      if (!lga.state || !lga.lga) return acc;
      const stateKey = lga.state.trim();
      if (!acc[stateKey]) acc[stateKey] = [];
      acc[stateKey].push(lga.lga);
      return acc;
    }, {});
  }, [lgas]);

  const availableLgas = formData.state ? groupedLgas[formData.state] || [] : [];

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'state') {
        setFormData({ ...formData, state: value, city: '' });
    } else {
        setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const payload = {
        items: cartItems.map(item => ({
          part_id: item.id,
          quantity: item.quantity,
          item_type: item.type || 'part' // <--- CRITICAL FIX: Send item type
        })),
        shipping_address: {
          full_name: formData.full_name,
          email: formData.email,
          phone: formData.phone,
          address_line1: formData.address_line1,
          city: formData.city,
          state: formData.state
        },
        payment_method: paymentMethod === 'pay_on_delivery' ? 'pay_on_delivery' : 'online'
      };

      const response = await ordersApi.createOrder(payload);
      const orderId = response.order.id;

      if (paymentMethod === 'pay_on_delivery') {
          clearCart();
          navigate('/order-success');
      } else if (paymentMethod === 'online') {
          const payResponse = await paymentApi.initiateOrderPayment(orderId, saveCard);
          if (payResponse.payment_link) {
              clearCart();
              window.location.href = payResponse.payment_link; 
          } else {
              throw new Error("Failed to generate payment link.");
          }
      } else {
          const chargeResponse = await paymentApi.chargeSavedCard(orderId, paymentMethod);
          if (chargeResponse.success) {
              setSuccessState(true); 
              clearCart();
              setTimeout(() => {
                  navigate('/order-success');
              }, 1000);
          } else {
              throw new Error(chargeResponse.message || "Payment failed.");
          }
      }

    } catch (err) {
      console.error("Checkout Error:", err);
      setError(err.message || "Failed to place order.");
      setLoading(false); 
    }
  };

  const getButtonText = () => {
      if (successState) return 'Payment Successful! Redirecting...';
      if (loading) return 'Processing...';
      if (paymentMethod === 'pay_on_delivery') return 'Place Order';
      if (paymentMethod === 'online') return 'Proceed to Payment';
      return 'Pay Instantly'; 
  };

  if (cartItems.length === 0) return <Navigate to="/parts" replace />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  return (
    <div className="bg-gray-50 min-h-screen py-12">
      <div className="container mx-auto px-4 max-w-6xl">
        <h1 className="text-3xl font-bold text-gray-800 mb-8">Checkout</h1>

        <div className="flex flex-col lg:flex-row gap-8">
          
          {/* Left Column: Shipping & Payment */}
          <div className="lg:w-2/3 space-y-6">
            
            {/* Shipping Info */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                    <FaTruck className="text-primary" /> Shipping Information
                  </h2>
                  {user && (
                      <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded flex items-center gap-1">
                          <FaUser /> Loaded from Profile
                      </span>
                  )}
              </div>

              {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
                  {error}
                </div>
              )}

              <form id="checkout-form" onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                  <input type="text" name="full_name" required className="w-full px-4 py-2 border rounded-lg bg-gray-50 focus:bg-white transition-colors" value={formData.full_name} onChange={handleChange} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input type="email" name="email" required className="w-full px-4 py-2 border rounded-lg bg-gray-50 focus:bg-white transition-colors" value={formData.email} onChange={handleChange} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input type="tel" name="phone" required className="w-full px-4 py-2 border rounded-lg bg-gray-50 focus:bg-white transition-colors" value={formData.phone} onChange={handleChange} />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <input type="text" name="address_line1" required className="w-full px-4 py-2 border rounded-lg bg-gray-50 focus:bg-white transition-colors" value={formData.address_line1} onChange={handleChange} />
                </div>
                
                {/* --- DYNAMIC STATE/LGA --- */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                  <select 
                      name="state" 
                      required 
                      className="w-full px-4 py-2 border rounded-lg bg-white" 
                      value={formData.state} 
                      onChange={handleChange}
                  >
                    <option value="">Select State</option>
                    {Object.keys(groupedLgas).sort().map(state => (
                        <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">City / LGA</label>
                  <select 
                      name="city" 
                      required 
                      className="w-full px-4 py-2 border rounded-lg bg-white disabled:bg-gray-100" 
                      value={formData.city} 
                      onChange={handleChange}
                      disabled={!formData.state}
                  >
                    <option value="">Select LGA</option>
                    {availableLgas.map(lga => (
                        <option key={lga} value={lga}>{lga}</option>
                    ))}
                  </select>
                </div>
              </form>
            </div>

            {/* Payment Method (Unchanged) */}
            <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <FaCreditCard className="text-primary" /> Payment Method
                </h2>
                <div className="space-y-3">
                    {savedCards.length > 0 && (
                        <div className="mb-4">
                            <p className="text-xs font-bold text-gray-500 uppercase mb-2">Saved Cards</p>
                            {savedCards.map((card, idx) => (
                                <label key={idx} className={`flex items-center p-4 border rounded-lg cursor-pointer transition-all mb-2 ${paymentMethod === card.token ? 'border-primary bg-blue-50 ring-1 ring-primary' : 'border-gray-200 hover:bg-gray-50'}`}>
                                    <input type="radio" name="payment" value={card.token} checked={paymentMethod === card.token} onChange={(e) => setPaymentMethod(e.target.value)} className="h-5 w-5 text-primary" />
                                    <div className="ml-3 flex items-center gap-3">
                                        <FaCreditCard className="text-gray-600 text-xl" />
                                        <div><span className="block font-bold text-gray-800">{card.type.toUpperCase()} **** {card.last_4digits}</span><span className="text-xs text-gray-500">Expires: {card.expiry}</span></div>
                                    </div>
                                </label>
                            ))}
                        </div>
                    )}
                    <div className={`border rounded-lg transition-all ${paymentMethod === 'online' ? 'border-primary bg-blue-50 ring-1 ring-primary' : 'border-gray-200 hover:bg-gray-50'}`}>
                        <label className="flex items-center p-4 cursor-pointer">
                            <input type="radio" name="payment" value="online" checked={paymentMethod === 'online'} onChange={(e) => setPaymentMethod(e.target.value)} className="h-5 w-5 text-primary" />
                            <div className="ml-3"><span className="block font-bold text-gray-800 flex items-center gap-2"><FaLock className="text-green-600" /> Pay with New Card / Transfer</span><span className="text-sm text-gray-500">Instant payment via Card, Transfer, or USSD.</span></div>
                        </label>
                        {paymentMethod === 'online' && (
                            <div className="px-4 pb-4 ml-8 animate-fadeIn">
                                <div className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${saveCard ? 'bg-yellow-100 border-yellow-400' : 'bg-white border-gray-300'}`} onClick={() => setSaveCard(!saveCard)}>
                                    <div className={`text-xl ${saveCard ? 'text-primary' : 'text-gray-400'}`}>{saveCard ? <FaCheckSquare /> : <FaSquare />}</div>
                                    <div><span className="block font-bold text-gray-800 text-sm">Save Card for Future Payments</span><span className="text-xs text-gray-600">Securely save this card for faster checkout.</span></div>
                                </div>
                            </div>
                        )}
                    </div>
                    <label className={`flex items-center p-4 border rounded-lg cursor-pointer transition-all ${paymentMethod === 'pay_on_delivery' ? 'border-primary bg-blue-50 ring-1 ring-primary' : 'border-gray-200 hover:bg-gray-50'}`}>
                        <input type="radio" name="payment" value="pay_on_delivery" checked={paymentMethod === 'pay_on_delivery'} onChange={(e) => setPaymentMethod(e.target.value)} className="h-5 w-5 text-primary" />
                        <div className="ml-3"><span className="block font-bold text-gray-800 flex items-center gap-2"><FaMoneyBillWave className="text-gray-600" /> Pay on Delivery</span><span className="text-sm text-gray-500">Pay cash or transfer when you receive your items.</span></div>
                    </label>
                </div>
            </div>
          </div>

          {/* Right Column: Order Summary */}
          <div className="lg:w-1/3">
            <div className="bg-white rounded-lg shadow-sm p-6 sticky top-24">
              <h2 className="text-lg font-bold text-gray-800 mb-4 border-b pb-2">Order Summary</h2>
              <div className="space-y-4 mb-6 max-h-60 overflow-y-auto pr-2">
                {cartItems.map(item => (
                  <div key={item.id} className="flex justify-between text-sm">
                    <span className="text-gray-600">{item.quantity}x {item.itemname}</span>
                    <span className="font-medium text-gray-800">{helpers.formatCurrency(item.price * item.quantity, 'NGN')}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-gray-200 pt-4 mb-6 space-y-2">
                <div className="flex justify-between text-gray-600"><span>Subtotal</span><span>{helpers.formatCurrency(cartTotal, 'NGN')}</span></div>
                <div className="flex justify-between text-gray-600"><span>Shipping</span><span className="text-sm italic">Calculated at checkout</span></div>
                <div className="flex justify-between items-center pt-2 border-t border-dashed border-gray-200 mt-2">
                  <span className="text-lg font-bold text-gray-800">Total</span>
                  <span className="text-2xl font-bold text-primary">{helpers.formatCurrency(cartTotal, 'NGN')}</span>
                </div>
              </div>
              <button type="submit" form="checkout-form" disabled={loading || successState} className={`w-full font-bold py-3 px-4 rounded-lg transition duration-300 shadow-md flex items-center justify-center gap-2 ${successState ? 'bg-green-600 text-white' : 'bg-primary hover:bg-primary-dark text-white'} ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}>
                {loading ? <><FaSpinner className="animate-spin" /> {successState ? 'Redirecting...' : 'Processing...'}</> : <>{successState ? <FaCheckCircle /> : (paymentMethod === 'online' ? <FaLock /> : <FaMoneyBillWave />)} {getButtonText()}</>}
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default CheckoutPage;